import type { TypedSupabaseClient } from '../client';
import { chunkIds } from './fetch-all-rows';

type ArticleCategoryReadView =
  | 'authorized_article_categories'
  | 'storefront_catalog_article_categories'
  | 'pro_catalog_article_categories';

type ArticleCategoryRelation = {
  id: string;
  name: string;
  image_url: string | null;
  parent_id: string | null;
};

type UntypedCategoryRead = {
  select: (columns: string) => UntypedCategoryRead;
  in: (
    column: string,
    values: string[],
  ) => Promise<{
    data: ArticleCategoryRelation[] | null;
    error: unknown | null;
  }>;
};

type JoinedArticleCategoryRow<Row extends Record<string, unknown>> = Row & {
  article_categories: ArticleCategoryRelation | null;
};

/**
 * Rebuild the historical `article_categories(...)` embedded relation without
 * asking PostgREST to traverse the governed base table. The authorized view
 * has no declared FK relationship to every publishing table, so an explicit
 * second read is both portable and keeps the mapper-facing alias unchanged.
 */
export async function joinAuthorizedArticleCategories<
  Row extends Record<string, unknown>,
>(
  supabase: TypedSupabaseClient,
  rows: Row[],
): Promise<Array<JoinedArticleCategoryRow<Row>>> {
  return joinArticleCategoriesFromView(
    supabase,
    rows,
    'authorized_article_categories',
  );
}

export async function joinStorefrontCatalogArticleCategories<
  Row extends Record<string, unknown>,
>(
  supabase: TypedSupabaseClient,
  rows: Row[],
): Promise<Array<JoinedArticleCategoryRow<Row>>> {
  return joinArticleCategoriesFromView(
    supabase,
    rows,
    'storefront_catalog_article_categories',
  );
}

export async function joinProCatalogArticleCategories<
  Row extends Record<string, unknown>,
>(
  supabase: TypedSupabaseClient,
  rows: Row[],
): Promise<Array<JoinedArticleCategoryRow<Row>>> {
  return joinArticleCategoriesFromView(
    supabase,
    rows,
    'pro_catalog_article_categories',
  );
}

async function joinArticleCategoriesFromView<
  Row extends Record<string, unknown>,
>(
  supabase: TypedSupabaseClient,
  rows: Row[],
  readView: ArticleCategoryReadView,
): Promise<Array<JoinedArticleCategoryRow<Row>>> {
  const categoryIds = [
    ...new Set(
      rows
        .map((row) => row['article_category_id'])
        .filter(
          (value): value is string => typeof value === 'string' && !!value,
        ),
    ),
  ];

  const categoriesById = new Map<string, ArticleCategoryRelation>();
  for (const categoryIdChunk of chunkIds(categoryIds)) {
    const query = (
      supabase as unknown as {
        from: (table: string) => UntypedCategoryRead;
      }
    ).from(readView);
    const { data, error } = await query
      .select('id, name, image_url, parent_id')
      .in('id', categoryIdChunk);
    if (error) throw error;
    for (const category of data ?? [])
      categoriesById.set(category.id, category);
  }

  return joinRows(rows, categoriesById);
}

function joinRows<Row extends Record<string, unknown>>(
  rows: Row[],
  categoriesById: ReadonlyMap<string, ArticleCategoryRelation>,
): Array<JoinedArticleCategoryRow<Row>> {
  return rows.map((row) => {
    const categoryId = row['article_category_id'];
    return {
      ...row,
      // Preserve the old PostgREST relation alias consumed by both mappers.
      article_categories:
        typeof categoryId === 'string'
          ? (categoriesById.get(categoryId) ?? null)
          : null,
    };
  });
}
